import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { teacherColdPool } from "@/lib/api";
import { Snowflake, RefreshCw, CheckCircle2 } from "lucide-react";

/**
 * Teacher-Dashboard "Cold Pool" tile.
 *
 * Surfaces per-sub_topic cold-pool stats for a learner:
 *   - current cycle number (how many times the learner has fully cleared the pool)
 *   - retired vs active question counts
 *   - %-retired progress bar so the educator can see retention/mastery at a glance.
 *
 * Empty state: shown when the learner has never engaged with any sub_topic
 * (no `learner_sub_topic_cycle` rows yet).
 */
export default function ColdPoolTile({ teacherId, learnerId }) {
  const [data, setData] = useState(null);
  const [err, setErr]   = useState(null);

  useEffect(() => {
    let alive = true;
    teacherColdPool(teacherId, learnerId)
      .then(d => { if (alive) setData(d); })
      .catch(e => { if (alive) setErr(e?.response?.data?.detail || "Failed to load"); });
    return () => { alive = false; };
  }, [teacherId, learnerId]);

  if (err) {
    return (
      <Card className="rounded-xl p-4 bg-white border border-rose-200" data-testid="cold-pool-error">
        <div className="text-sm text-rose-700 font-semibold">Cold pool: {err}</div>
      </Card>
    );
  }
  if (!data) {
    return (
      <Card className="rounded-xl p-4 bg-white border border-slate-200" data-testid="cold-pool-loading">
        <div className="text-sm text-slate-400">Loading cold pool…</div>
      </Card>
    );
  }

  const rows = data.rows || [];
  const summary = data.summary || {};

  return (
    <Card className="rounded-xl p-4 bg-white border border-cyan-200" data-testid="cold-pool-tile">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-teacher text-lg font-semibold text-slate-900 flex items-center gap-2">
          <Snowflake className="w-4 h-4 text-cyan-600" /> Cold Pool
        </h2>
        <div className="text-xs text-slate-500 flex items-center gap-3">
          <span data-testid="cold-pool-summary-tracked">
            <strong className="text-slate-700">{summary.total_sub_topics_tracked || 0}</strong> tracked
          </span>
          <span data-testid="cold-pool-summary-cycle">
            highest cycle <strong className="text-slate-700">{summary.highest_cycle || 1}</strong>
          </span>
        </div>
      </div>

      {rows.length === 0 ? (
        <div
          className="text-center text-slate-400 py-6 text-sm"
          data-testid="cold-pool-empty"
        >
          No sub-topics engaged yet — retirement will start once {data.learner_name || "this learner"} answers their first correct first-attempt question.
        </div>
      ) : (
        <div className="space-y-3" data-testid="cold-pool-rows">
          {rows.map((r) => {
            const pct = r.retired_pct;
            const isFull = pct === 100;
            const rowKey = `${r.subject}:${r.sub_topic}`;
            return (
              <div
                key={rowKey}
                className="border border-slate-200 rounded-lg p-3"
                data-testid={`cold-pool-row-${r.subject}-${r.sub_topic}`}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="text-xs uppercase tracking-wider text-cyan-700 font-semibold">{r.subject}</div>
                    <div className="text-sm font-teacher font-semibold text-slate-900 truncate" title={r.sub_topic}>
                      {r.sub_topic}
                    </div>
                  </div>
                  <div className="text-right text-xs text-slate-600 flex flex-col items-end gap-0.5 shrink-0">
                    <span className="font-bold text-slate-900" data-testid={`cold-pool-count-${r.sub_topic}`}>
                      {r.retired_count}/{r.pool_total}
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <RefreshCw className="w-3 h-3 text-slate-400" />
                      cycle <strong className="text-slate-700" data-testid={`cold-pool-cycle-${r.sub_topic}`}>{r.cycle}</strong>
                    </span>
                  </div>
                </div>
                <div className="mt-2 h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full ${isFull ? "bg-emerald-500" : "bg-cyan-500"} transition-all`}
                    style={{ width: `${pct}%` }}
                    data-testid={`cold-pool-progress-${r.sub_topic}`}
                  />
                </div>
                {isFull && (
                  <div className="mt-1.5 text-[11px] text-emerald-700 font-semibold flex items-center gap-1" data-testid={`cold-pool-full-${r.sub_topic}`}>
                    <CheckCircle2 className="w-3 h-3" />
                    Full cycle retired — pool reshuffles next attempt
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}
